import { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'dark' | 'light';

export interface AccentColor {
  id: string;
  name: string;
  value: string;
  tailwind: string;
  tailwindLight: string;
  tailwindBg: string;
  tailwindBgLight: string;
  tailwindBorder: string;
  tailwindText: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ACCENT_COLORS: AccentColor[] = [
  {
    id: 'teal',
    name: 'Teal',
    value: '#14B8A6',
    tailwind: 'bg-teal-500',
    tailwindLight: 'bg-teal-400',
    tailwindBg: 'bg-teal-500/10',
    tailwindBgLight: 'bg-teal-50',
    tailwindBorder: 'border-teal-500',
    tailwindText: 'text-teal-500',
  },
  {
    id: 'rose',
    name: 'Rose',
    value: '#F43F5E',
    tailwind: 'bg-rose-500',
    tailwindLight: 'bg-rose-400',
    tailwindBg: 'bg-rose-500/10',
    tailwindBgLight: 'bg-rose-50',
    tailwindBorder: 'border-rose-500',
    tailwindText: 'text-rose-500',
  },
  {
    id: 'amber',
    name: 'Amber',
    value: '#F59E0B',
    tailwind: 'bg-amber-500',
    tailwindLight: 'bg-amber-400',
    tailwindBg: 'bg-amber-500/10',
    tailwindBgLight: 'bg-amber-50',
    tailwindBorder: 'border-amber-500',
    tailwindText: 'text-amber-500',
  },
  {
    id: 'emerald',
    name: 'Emerald',
    value: '#10B981',
    tailwind: 'bg-emerald-500',
    tailwindLight: 'bg-emerald-400',
    tailwindBg: 'bg-emerald-500/10',
    tailwindBgLight: 'bg-emerald-50',
    tailwindBorder: 'border-emerald-500',
    tailwindText: 'text-emerald-500',
  },
  {
    id: 'orange',
    name: 'Orange',
    value: '#F97316',
    tailwind: 'bg-orange-500',
    tailwindLight: 'bg-orange-400',
    tailwindBg: 'bg-orange-500/10',
    tailwindBgLight: 'bg-orange-50',
    tailwindBorder: 'border-orange-500',
    tailwindText: 'text-orange-500',
  },
  {
    id: 'pink',
    name: 'Pink',
    value: '#EC4899',
    tailwind: 'bg-pink-500',
    tailwindLight: 'bg-pink-400',
    tailwindBg: 'bg-pink-500/10',
    tailwindBgLight: 'bg-pink-50',
    tailwindBorder: 'border-pink-500',
    tailwindText: 'text-pink-500',
  },
  {
    id: 'cyan',
    name: 'Cyan',
    value: '#06B6D4',
    tailwind: 'bg-cyan-500',
    tailwindLight: 'bg-cyan-400',
    tailwindBg: 'bg-cyan-500/10',
    tailwindBgLight: 'bg-cyan-50',
    tailwindBorder: 'border-cyan-500',
    tailwindText: 'text-cyan-500',
  },
  {
    id: 'lime',
    name: 'Lime',
    value: '#84CC16',
    tailwind: 'bg-lime-500',
    tailwindLight: 'bg-lime-400',
    tailwindBg: 'bg-lime-500/10',
    tailwindBgLight: 'bg-lime-50',
    tailwindBorder: 'border-lime-500',
    tailwindText: 'text-lime-500',
  },
];

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  isDark: boolean;
  accent: AccentColor;
  setAccentById: (id: string) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  setTheme: () => {},
  isDark: true,
  accent: ACCENT_COLORS[0],
  setAccentById: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    return (localStorage.getItem('bk-theme') as Theme) ?? 'dark';
  });

  const [accentId, setAccentIdState] = useState<string>(() => {
    return localStorage.getItem('bk-accent') ?? 'teal';
  });

  const accent = ACCENT_COLORS.find(c => c.id === accentId) ?? ACCENT_COLORS[0];

  const setTheme = (t: Theme) => {
    setThemeState(t);
    localStorage.setItem('bk-theme', t);
  };

  const setAccentById = (id: string) => {
    setAccentIdState(id);
    localStorage.setItem('bk-accent', id);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-appearance', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.style.setProperty('--accent', accent.value);
  }, [accent]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark: theme === 'dark', accent, setAccentById }}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => useContext(ThemeContext);
